import { Fragment, useState } from "react";
import { api } from "../../api";
import { useAuth } from "../../auth/AuthContext";
import { Empty, ErrorNote, formatBytes } from "../../components/Feedback";
import QuotaNote from "../../components/QuotaNote";
import UploadPanel from "../../components/UploadPanel";
import { Badge, Btn, ConfirmDialog } from "../../components/Shared";
import { useApi } from "../../useApi";
import FileVersions from "./FileVersions";

// The Files tab: upload panel (the dataset's editors only), the file table with both checksums
// (MD5, and the iRODS form sha2:<base64 SHA-256>), and the dataset's iRODS AVU export
// (an `imeta add` script; knowledge/reference/irods-compatibility.md). "Versions" opens the
// file's versions below its row (FileVersions.jsx): a replace keeps the old bytes. Large files go
// straight to S3 from the size GET /uploads/config names (UploadPanel's directMinBytes). The delete
// dialog shows GET /files/{id}/deletion-impact: a built database is marked stale, a hosted one keeps
// the rows (services/source_deletes.py). Props: see DatasetPage.jsx.
//
// A file's ROLE (task 2026-10-20 "protocols are files"): a protocol is an ordinary file with the
// role protocol (an .eln is one by its name). The dataset's editors set the role of a file that is
// no table with a select (PATCH /files/{id}/role; "" = from its name, null on the wire, and its
// option says what the name gives, e.g. "from the file name (protocol)"); everybody sees a
// protocol as a badge. A tabular file's role is `table` once a build loads it. The select is a
// plain <select> styled like Shared.jsx's (a byte-identical copy whose empty option reads
// "— select —", which would hide what an unset role means).

// The roles a person may set (services/irods.SETTABLE_ROLES); "" = what the name and kind give.
export const ROLE_OPTIONS = [
  { value: "protocol", label: "protocol" },
  { value: "raw", label: "raw data" },
  { value: "attachment", label: "attachment" },
];

const roleSelect = {
  background: "#ffffff", border: "1px solid #d1d5db", borderRadius: 8, color: "#1a1a1a",
  padding: "6px 10px", fontSize: 13, width: "100%", boxSizing: "border-box", fontFamily: "inherit",
  cursor: "pointer",
};

// What an unset role means for this file: the role its name and the dataset's kind give.
export function autoRoleLabel(file) {
  const auto = file.role_set ? null : file.role;
  return `from the file name${auto ? ` (${(ROLE_OPTIONS.find(o => o.value === auto) || {}).label || auto})` : ""}`;
}

function RoleControl({ file, canEdit, onChanged, onError }) {
  const [saving, setSaving] = useState(false);
  const badge = file.role === "protocol" ? <> <Badge color="amber">protocol</Badge></> : null;
  if (!canEdit) return badge;
  const change = async e => {
    setSaving(true);
    try {
      await api.patch(`/files/${file.id}/role`, { role: e.target.value || null });
      onChanged();
    } catch (err) {
      onError(err);
    } finally {
      setSaving(false);
    }
  };
  return (
    <>
      {badge}
      <div style={{ marginTop: 6, maxWidth: 260 }}>
        <select aria-label={`Role of ${file.filename}`} value={file.role_set || ""} disabled={saving}
                onChange={change} style={roleSelect}
                title={file.role_set ? `Role set by hand: ${file.role}`
                                     : `Role from the file's name: ${file.role || "raw"} (choose one to set it)`}>
          <option value="">{autoRoleLabel(file)}</option>
          {ROLE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>
    </>
  );
}

export default function FilesTab({ dataset, files, canEdit, reloadFiles }) {
  const [error, setError] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [openVersions, setOpenVersions] = useState(null);
  const { user } = useAuth();
  const uploads = useApi(canEdit ? "/uploads/config" : null);
  // What the delete does to a built or hosted database (the dialog says which policy applies).
  const impact = useApi(confirm ? `/files/${confirm.id}/deletion-impact` : null);

  const download = f => api.download(`/files/${f.id}/download`, f.filename).catch(setError);
  const exportAvu = () => api.download(`/datasets/${dataset.id}/export/avu?format=imeta`,
                                       `dataset-${dataset.id}-avu.sh`).catch(setError);
  const remove = async f => {
    setConfirm(null);
    try {
      await api.del(`/files/${f.id}`);
      reloadFiles();
    } catch (err) {
      setError(err);
    }
  };

  return (
    <div>
      {canEdit && <QuotaNote projectId={dataset.project_id} />}
      {canEdit && <UploadPanel datasetId={dataset.id} onUploaded={() => reloadFiles()}
                                directMinBytes={(uploads.data && uploads.data.direct_upload_min_bytes) || 0} />}
      <ErrorNote error={error} />
      {files.length > 0 && (
        <div style={{ display: "flex", justifyContent: "flex-end", margin: "0 0 10px" }}>
          <Btn variant="secondary" onClick={exportAvu}>Export iRODS AVUs</Btn>
        </div>
      )}
      {files.length === 0 ? <Empty>No files yet.</Empty> : (
        <div style={{ overflowX: "auto", background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12 }}>
          <table className="responsive-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
            <caption style={{ position: "absolute", left: -9999 }}>Files of {dataset.name}</caption>
            <thead>
              <tr style={{ textAlign: "left", color: "#6b7280", fontSize: 12, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                <th scope="col" style={{ padding: "10px 14px" }}>File</th>
                <th scope="col" style={{ padding: "10px 14px" }}>Size</th>
                <th scope="col" style={{ padding: "10px 14px" }}>Columns</th>
                <th scope="col" style={{ padding: "10px 14px" }}>Checksums</th>
                <th scope="col" style={{ padding: "10px 14px" }}><span style={{ position: "absolute", left: -9999 }}>Actions</span></th>
              </tr>
            </thead>
            <tbody>
              {files.map(f => (
                <Fragment key={f.id}>
                <tr style={{ borderTop: "1px solid #f3f4f6" }}>
                  <td data-label="File" style={{ padding: "10px 14px" }}>
                    {f.filename} {f.is_tabular && <Badge color="green">table</Badge>}
                    {(f.version || 1) > 1 && <> <Badge color="gray">v{f.version}</Badge></>}
                    {f.inference_error && <div style={{ fontSize: 12, color: "#92400e" }}>{f.inference_error}</div>}
                    {!f.is_tabular && <RoleControl file={f} canEdit={canEdit} onChanged={() => reloadFiles()}
                                                   onError={setError} />}
                  </td>
                  <td data-label="Size" style={{ padding: "10px 14px", whiteSpace: "nowrap" }}>{formatBytes(f.size)}</td>
                  <td data-label="Columns" style={{ padding: "10px 14px" }}>
                    {f.is_tabular ? `${f.column_count} col · ${f.row_count ?? "?"} rows` : "—"}
                  </td>
                  <td data-label="Checksums" style={{ padding: "10px 14px", fontFamily: "'JetBrains Mono', monospace", fontSize: 12, wordBreak: "break-all" }}>
                    <div>md5:{f.md5}</div>
                    <div style={{ color: f.checksum ? undefined : "#6b7280" }}>
                      {f.checksum || "sha2: not recorded (uploaded before SHA-256; replace to add it)"}
                    </div>
                  </td>
                  <td style={{ padding: "10px 14px", whiteSpace: "nowrap", textAlign: "right" }}>
                    <span style={{ display: "inline-flex", gap: 8 }}>
                      <Btn variant="secondary" onClick={() => download(f)}>Download</Btn>
                      <Btn variant="secondary" onClick={() => setOpenVersions(openVersions === f.id ? null : f.id)}>
                        {openVersions === f.id ? "Hide versions" : "Versions"}
                      </Btn>
                      {canEdit && <Btn variant="danger" onClick={() => setConfirm(f)}>Delete</Btn>}
                    </span>
                  </td>
                </tr>
                {openVersions === f.id && (
                  <tr>
                    <td colSpan={5} style={{ padding: "0 14px 10px", background: "#f9fafb" }}>
                      <FileVersions file={f} canEdit={canEdit} isAdmin={!!user?.is_admin}
                                    onRestored={() => reloadFiles()} />
                    </td>
                  </tr>
                )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {confirm && (
        <ConfirmDialog
          title="Delete file"
          confirmWord={confirm.filename}
          onConfirm={() => remove(confirm)}
          onClose={() => setConfirm(null)}
        >
          <p style={{ margin: 0, fontSize: 14, color: "#374151" }}>
            Delete <strong>{confirm.filename}</strong>? Its column dictionary is deleted with it. Its
            bytes (every version) are kept until an admin purges them.
          </p>
          {impact.data && (
            <p data-testid="deletion-impact" style={{ margin: "10px 0 0", fontSize: 14, color: impact.data.versions.length ? "#92400e" : "#374151" }}>
              {impact.data.message}
            </p>
          )}
        </ConfirmDialog>
      )}
    </div>
  );
}
